import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";
import { getMyOrgId } from "./org";
import { optimizeImageForStorage } from "./utils/imageOptimize";
import {
  STORAGE_BUCKET,
  buildVisualProductKey,
  deleteVisual,
  listVisualsForOrg,
  upsertVisual,
  VISUAL_LIBRARY_PRIORITY_CATEGORIES,
  VisualLibraryRecord,
} from "./visualLibrary";
import "./VisualLibraryPage.css";

type ProductOption = {
  id: string | null; // pricing_items2 id (Decking/Railing); null for Skirting/Lattice
  value: string;
  label: string;
};

type Props = {
  productOptionsByCategory: Record<string, ProductOption[]>;
};

const CATEGORIES = VISUAL_LIBRARY_PRIORITY_CATEGORIES;

// Categories with a fixed set of id-less style cards (not pricing_items2-id
// keyed). Skirting has two stable slots — Vertical and Horizontal — each
// keyed by product_key via buildVisualProductKey(category, value). Lattice and
// Fasteners are id-keyed (one card per option) so they are NOT listed here.
const FIXED_CARD_CATEGORIES: Record<string, ProductOption[]> = {
  Skirting: [
    { id: null, value: "Vertical Skirting", label: "Vertical Skirting" },
    { id: null, value: "Horizontal Skirting", label: "Horizontal Skirting" },
  ],
  // Stairs: two style slots keyed by product_key (stairs:standard_staircase /
  // stairs:mitered_treads). The matcher picks Mitered when a "Mitered Treads"
  // add-item is present, else Standard.
  Stairs: [
    { id: null, value: "Standard Staircase", label: "Standard Staircase" },
    { id: null, value: "Mitered Treads", label: "Mitered Treads" },
  ],
  // Demo: one photo (demo:demo), shown whenever demo is part of the job.
  Demo: [{ id: null, value: "Demo", label: "Demo (Dump Truck)" }],
  // Bench: two style slots keyed to the bench type (bench:12_flat / bench:18_flat).
  Bench: [
    { id: null, value: "12 Flat", label: '12" Flat Bench' },
    { id: null, value: "18 Flat", label: '18" Flat Bench' },
  ],
};

function safeFileName(value: string): string {
  return (
    (value || "photo")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "") || "photo"
  );
}

function uniqueOptions(options: ProductOption[]): ProductOption[] {
  const seen = new Set<string>();
  return options.filter((option) => {
    const key = (option.value || "").trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : "Something went wrong.";
}

export default function VisualLibraryPage({ productOptionsByCategory }: Props) {
  const [orgId, setOrgId] = useState<string | null>(null);
  const [orgResolved, setOrgResolved] = useState(false);
  const [category, setCategory] = useState<string>("");
  const [records, setRecords] = useState<VisualLibraryRecord[]>([]);
  const [recordsLoaded, setRecordsLoaded] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [busyKeys, setBusyKeys] = useState<Set<string>>(new Set());
  const [captions, setCaptions] = useState<Record<string, string>>({});
  const [error, setError] = useState<string>("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const id = await getMyOrgId();
        if (alive) setOrgId(id);
      } finally {
        if (alive) setOrgResolved(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Cards for the active category. Decking/Railing => one per product;
  // Skirting/Lattice => a single style card.
  const cards: ProductOption[] = useMemo(() => {
    if (!category) return [];
    if (FIXED_CARD_CATEGORIES[category]) return FIXED_CARD_CATEGORIES[category];
    return uniqueOptions(productOptionsByCategory[category] || []);
  }, [category, productOptionsByCategory]);

  const cardKey = (option: ProductOption): string =>
    option.id ? `id:${option.id}` : `key:${buildVisualProductKey(category, option.value)}`;

  const recordFor = (option: ProductOption): VisualLibraryRecord | undefined => {
    if (option.id) return records.find((r) => r.productId === option.id);
    const productKey = buildVisualProductKey(category, option.value);
    return records.find((r) => !r.productId && r.productKey === productKey);
  };

  const captionValue = (option: ProductOption): string => {
    const ck = cardKey(option);
    if (ck in captions) return captions[ck];
    return recordFor(option)?.caption || "";
  };

  const markBusy = (ck: string, on: boolean) =>
    setBusyKeys((prev) => {
      const next = new Set(prev);
      if (on) next.add(ck);
      else next.delete(ck);
      return next;
    });

  const upsertLocal = (rec: VisualLibraryRecord) =>
    setRecords((prev) => [...prev.filter((r) => r.id !== rec.id), rec]);

  const ensureRecords = async (id: string) => {
    if (recordsLoaded) return;
    setLoadingRecords(true);
    setError("");
    try {
      const rows = await listVisualsForOrg(id);
      setRecords(rows);
      setRecordsLoaded(true);
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleCategoryChange = (next: string) => {
    setCategory(next);
    if (next && orgId) void ensureRecords(orgId);
  };

  const handleFile = async (option: ProductOption, file: File) => {
    if (!orgId) {
      setError("No organization found for your account.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      window.alert("Please choose an image file.");
      return;
    }
    const ck = cardKey(option);
    const productKey = buildVisualProductKey(category, option.value);
    const existing = recordFor(option);
    markBusy(ck, true);
    setError("");
    try {
      const optimized = await optimizeImageForStorage(file);
      const ext = optimized.contentType.includes("webp") ? "webp" : "jpg";
      const objectPath = `${category.toLowerCase()}/${Date.now()}-${safeFileName(option.value)}.${ext}`;

      const { error: upErr } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(objectPath, optimized.blob, {
          contentType: optimized.contentType,
          upsert: false,
          cacheControl: "3600",
        });
      if (upErr) throw upErr;

      const { data: pub } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(objectPath);
      const imageRef = pub?.publicUrl || "";

      const rec = await upsertVisual({
        orgId,
        category,
        productId: option.id,
        productKey,
        displayName: option.value,
        imagePath: objectPath,
        imageRef,
        caption: captions[ck] ?? existing?.caption ?? "",
      });

      // Replacing: drop the previous object so the bucket doesn't accumulate orphans.
      if (existing?.imagePath && existing.imagePath !== objectPath) {
        await supabase.storage.from(STORAGE_BUCKET).remove([existing.imagePath]).catch(() => {});
      }

      upsertLocal(rec);
      setCaptions((prev) => ({ ...prev, [ck]: rec.caption || "" }));
    } catch (e) {
      setError(errMessage(e));
    } finally {
      markBusy(ck, false);
    }
  };

  const handleRemove = async (option: ProductOption) => {
    const existing = recordFor(option);
    if (!existing) return;
    if (!window.confirm("Remove this photo? This deletes it from the library and storage.")) return;
    const ck = cardKey(option);
    markBusy(ck, true);
    setError("");
    try {
      await deleteVisual({ id: existing.id, imagePath: existing.imagePath });
      setRecords((prev) => prev.filter((r) => r.id !== existing.id));
      setCaptions((prev) => ({ ...prev, [ck]: "" }));
    } catch (e) {
      setError(errMessage(e));
    } finally {
      markBusy(ck, false);
    }
  };

  const handleCaptionBlur = async (option: ProductOption) => {
    const existing = recordFor(option);
    if (!existing || !orgId) return; // no photo yet => nothing to persist; draft is reused on upload
    const ck = cardKey(option);
    const value = (captions[ck] ?? "").trim();
    if ((existing.caption || "") === value) return;
    markBusy(ck, true);
    try {
      const rec = await upsertVisual({
        orgId,
        category,
        productId: existing.productId ?? option.id,
        productKey: existing.productKey,
        displayName: existing.displayName,
        imagePath: existing.imagePath,
        imageRef: existing.imageRef,
        caption: value,
      });
      upsertLocal(rec);
    } catch (e) {
      setError(errMessage(e));
    } finally {
      markBusy(ck, false);
    }
  };

  const filledCount = useMemo(
    () => cards.filter((opt) => !!recordFor(opt)?.imageRef).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cards, records]
  );

  const renderGrid = () => {
    if (!category) {
      return <div className="vlg-placeholder">Pick a category to start curating photos.</div>;
    }
    if (!orgResolved || loadingRecords) {
      return <div className="vlg-placeholder">Loading library…</div>;
    }
    if (!orgId) {
      return <div className="vlg-placeholder">No organization found for your account.</div>;
    }
    if (cards.length === 0) {
      return <div className="vlg-placeholder">No products found in this category.</div>;
    }
    return (
      <div className="vlg-grid">
        {cards.map((option) => {
          const ck = cardKey(option);
          const rec = recordFor(option);
          const hasPhoto = !!rec?.imageRef;
          const busy = busyKeys.has(ck);
          return (
            <article
              key={ck}
              className={`vlg-card ${hasPhoto ? "vlg-card--filled" : "vlg-card--empty"} ${
                busy ? "is-busy" : ""
              }`}
            >
              <div className="vlg-card__media">
                {hasPhoto ? (
                  <>
                    <img className="vlg-card__img" src={rec!.imageRef} alt={option.label} />
                    <div className="vlg-card__overlay">
                      <label className="vlg-btn vlg-btn--ghost">
                        Replace
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = "";
                            if (f) void handleFile(option, f);
                          }}
                        />
                      </label>
                      <button
                        type="button"
                        className="vlg-btn vlg-btn--danger"
                        onClick={() => void handleRemove(option)}
                      >
                        Remove
                      </button>
                    </div>
                  </>
                ) : (
                  <label className="vlg-dropzone">
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        if (f) void handleFile(option, f);
                      }}
                    />
                    <span className="vlg-dropzone__icon" aria-hidden>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                      </svg>
                    </span>
                    <span className="vlg-dropzone__text">Add photo</span>
                  </label>
                )}
                {busy && (
                  <div className="vlg-card__spinner" aria-hidden>
                    <span />
                  </div>
                )}
              </div>
              <div className="vlg-card__body">
                <div className="vlg-card__name" title={option.label}>
                  {option.label}
                </div>
                <input
                  className="vlg-card__caption"
                  placeholder="Add a caption…"
                  value={captionValue(option)}
                  disabled={busy}
                  onChange={(e) => setCaptions((prev) => ({ ...prev, [ck]: e.target.value }))}
                  onBlur={() => void handleCaptionBlur(option)}
                />
              </div>
            </article>
          );
        })}
      </div>
    );
  };

  return (
    <section className="vlg-page">
      <header className="vlg-header">
        <div className="vlg-header__titles">
          <div className="vlg-eyebrow">Proposal visuals</div>
          <h2 className="vlg-title">Visual Library</h2>
          <p className="vlg-subtitle">
            Curate one photo per product. Filled and empty cards make coverage obvious at a glance.
          </p>
        </div>
        <div className="vlg-header__controls">
          <label className="vlg-category">
            <span className="vlg-category__label">Category</span>
            <select
              className="vlg-category__select"
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              <option value="">Choose a category…</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          {category && orgId && !loadingRecords ? (
            <div className="vlg-coverage">
              {filledCount}/{cards.length} with photos
            </div>
          ) : null}
        </div>
      </header>

      {error ? <div className="vlg-error">{error}</div> : null}

      {renderGrid()}
    </section>
  );
}
